import {
  FormControl,
  Components as JoyComponents,
  Theme as JoyTheme,
} from '@mui/joy';
import type { RadioProps as JoyRadioProps } from '@mui/joy/Radio';
import JoyRadio from '@mui/joy/Radio';
import JoyRadioGroup, {
  RadioGroupProps as JoyRadioGroupProps,
} from '@mui/joy/RadioGroup';
import { mergeProps, mergeRefs, useObjectRef } from '@react-aria/utils';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { Description, Label } from './fieldset';
import {
  ComposedFormControllerProps,
  FormController,
  FormControllerProps,
  useFormController,
} from './form';

export const overrides: JoyComponents<JoyTheme>['JoyRadio'] = {};

export const Radio = forwardRef<HTMLDivElement, JoyRadioProps>(function Radio(
  { ...props },
  ref,
) {
  const objectRef = useObjectRef(ref);
  const mergedRef = mergeRefs(ref, objectRef);
  return (
    <JoyRadio {...mergeProps(props, { ref: mergedRef })} data-slot='control' />
  );
});

const layoutClasses = [
  // Base layout
  'grid grid-cols-[1.125rem_1fr] items-center gap-x-4 gap-y-1 sm:grid-cols-[1rem_1fr]',
  // Control layout
  '[&>[data-slot=control]]:col-start-1 [&>[data-slot=control]]:row-start-1 [&>[data-slot=control]]:justify-self-center',
  // Label layout
  '[&>[data-slot=label]]:col-start-2 [&>[data-slot=label]]:row-start-1 [&>[data-slot=label]]:justify-self-start',
  // Description layout
  '[&>[data-slot=description]]:col-start-2 [&>[data-slot=description]]:row-start-2',
  // With description
  '[&_[data-slot=label]]:has-[[data-slot=description]]:font-medium',
];
export { layoutClasses as radioLayoutClasses };

export const RadioGroup = forwardRef<
  HTMLDivElement,
  JoyRadioGroupProps & { controlled?: boolean }
>(function RadioGroup({ controlled = true, ...props }, ref) {
  const controller = useFormController();
  const objectRef = useObjectRef(ref);
  const mergedRef = mergeRefs(ref, objectRef);

  return (
    <JoyRadioGroup
      {...mergeProps(
        props,
        controller && controlled
          ? { ...controller?.field, value: controller.field.value }
          : { ref: mergedRef },
      )}
      data-slot='control'
      slotProps={{
        root: {
          sx: { '--unstable_RadioGroup-margin': 0 },
          className: clsx([
            // Basic groups
            'space-y-3',

            // With descriptions
            'has-[[data-slot=description]]:space-y-4 [&_[data-slot=label]]:has-[[data-slot=description]]:font-medium',
          ]),
        },
      }}
    />
  );
});

export function RadioGroupField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  children,
  ...props
}: Omit<FormControllerProps<T, TName>, 'children'> & {
  children: React.ReactNode;
}) {
  return (
    <FormController
      {...props}
      slotProps={{
        root: {
          'data-slot': 'control',
        },
      }}
    >
      <RadioGroup>{children}</RadioGroup>
    </FormController>
  );
}

export function RadioField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  ...props
}: Omit<JoyRadioProps, 'ref' | keyof ComposedFormControllerProps<T, TName>> & {
  label?: React.ReactNode;
  description?: React.ReactNode;
}) {
  return (
    <FormControl
      slotProps={{
        root: {
          'data-slot': 'field',
        },
      }}
    >
      <div className={clsx(layoutClasses)}>
        <Radio
          {...props}
          sx={{
            '--RadioGroup-gap': 0,
          }}
        />
        {label && <Label>{label}</Label>}
        {description && <Description>{description}</Description>}
      </div>
    </FormControl>
  );
}
